import { loadQuery } from 'react-relay';
import type { PreloadedQuery } from 'react-relay';
import { createOperationDescriptor } from 'relay-runtime';
import type { IEnvironment } from 'relay-runtime';

import { getAtlAttributionHeader } from '@confluence/query-preloader-tools/entry-points/getAtlAttributionHeader.ts';

import {
	RelayError,
	isRelayError,
	isRelayGraphQLError,
	type RelayGraphQLError,
} from '@confluence/relay-errors/entry-points/RelayError.ts';

import ConversationQueryArtifact, {
	type ObjectCommentContainerPreloadObjectCommentsQuery as ConversationQuery,
	type ObjectCommentContainerPreloadObjectCommentsQuery$variables as ConversationQuery$variables,
} from './components/__generated__/ObjectCommentContainerPreloadObjectCommentsQuery.graphql.ts';

/**
 * A single conversation execution. Call outside React render; readers share `reference`.
 * The owner must dispose it after its final reader has released it.
 */
export class ConversationQueryReference {
	readonly reference: PreloadedQuery<ConversationQuery>;
	readonly ready: Promise<void>;
	readonly identifier: string;
	readonly variables: ConversationQuery$variables;
	error: RelayError | undefined;
	settled = false;
	failed = false;
	disposed = false;
	private finish: () => void;
	private unsubscribe: () => void = () => {};

	constructor(
		readonly environment: IEnvironment,
		variables: ConversationQuery$variables,
		fetchPolicy: 'network-only' | 'store-or-network' = 'store-or-network',
	) {
		const operation = createOperationDescriptor(ConversationQueryArtifact, variables);
		this.identifier = operation.request.identifier;
		this.variables = { ...variables, ...operation.request.variables };
		let resolveReady: () => void = () => {};
		this.ready = new Promise<void>((resolve) => {
			resolveReady = resolve;
		});
		this.finish = () => {
			this.settled = true;
			resolveReady();
		};
		this.reference = loadQuery<ConversationQuery>(
			environment,
			ConversationQueryArtifact,
			this.variables,
			{
				fetchPolicy,
				// Preserve the attribution supplied by the previous route preloader.
				networkCacheConfig: { metadata: { headers: getAtlAttributionHeader() } },
			},
		);
		let graphQLErrors: ReadonlyArray<RelayGraphQLError> = [];
		const subscription = this.reference.source?.subscribe({
			next: (response) => {
				graphQLErrors = [
					...graphQLErrors,
					...(Array.isArray(response) ? response : [response]).flatMap(
						(payload) => payload.errors ?? [],
					),
				];
				if (graphQLErrors.length) {
					this.error = new RelayError({ graphQLErrors });
				}
			},
			error: (failure: unknown) => {
				this.failed = true;
				const payloads = Array.isArray(failure)
					? failure.filter(isRelayGraphQLError)
					: isRelayGraphQLError(failure)
						? [failure]
						: failure instanceof Error &&
							  'source' in failure &&
							  typeof failure.source === 'object' &&
							  failure.source !== null &&
							  'errors' in failure.source &&
							  Array.isArray(failure.source.errors)
							? failure.source.errors.filter(isRelayGraphQLError)
							: [];
				this.error = isRelayError(failure)
					? failure
					: new RelayError({
							graphQLErrors: [...graphQLErrors, ...payloads],
							networkError: payloads.length
								? undefined
								: failure instanceof Error
									? failure
									: new Error(String(failure)),
						});
				this.finish();
			},
			complete: this.finish,
		});
		this.unsubscribe = () => subscription?.unsubscribe();
		if (!this.reference.source) this.finish();
	}

	dispose(): void {
		if (this.disposed) return;
		this.disposed = true;
		this.unsubscribe();
		this.reference.dispose();
		// Cancellation must also release a preloader waiting for completion.
		this.finish();
	}
}
